/**
 * CPTReviewPanel Component
 *
 * Displays CPT code comparison and billing support information
 */

import React from 'react';
import {
  Check as CheckIcon,
  ThumbDown as ConcernIcon,
  ThumbUp as StrengthIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { BillingSupport } from '../../types';

interface CPTReviewPanelProps {
  /** AI recommended CPT codes */
  recommendedCodes: string[] | null | undefined;
  /** Provider's CPT codes */
  providerCodes: string[] | null | undefined;
  /** Whether codes match */
  cptMatch: boolean | undefined;
  /** Billing support analysis */
  billingSupport: BillingSupport | null;
  /** Callback to update CPT code */
  onUpdateCPT?: (newCode: string) => void;
}

export const CPTReviewPanel: React.FC<CPTReviewPanelProps> = ({
  recommendedCodes,
  providerCodes,
  cptMatch,
  billingSupport,
  onUpdateCPT,
}) => {
  const theme = useTheme();

  const hasRecommendedCodes = recommendedCodes && recommendedCodes.length > 0;
  const hasProviderCodes = providerCodes && providerCodes.length > 0;
  const hasMismatch = cptMatch === false && hasRecommendedCodes && hasProviderCodes;
  const confidence = billingSupport?.confidence ?? 0;
  const confidencePercent = Math.round(confidence * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          CPT Review
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* CPT Comparison */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {/* Provider Codes */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
            >
              Provider {hasProviderCodes && providerCodes.length > 1 ? 'Codes' : 'Code'}
            </Typography>
            {hasProviderCodes ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {providerCodes.map((code, index) => (
                  <Typography
                    key={index}
                    variant="h6"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                    }}
                  >
                    {code}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                }}
              >
                —
              </Typography>
            )}
          </Box>

          {/* Match Status */}
          <Box sx={{ px: 2 }}>
            {cptMatch === true ? (
              <Chip
                icon={<CheckIcon />}
                label="Match"
                color="success"
                size="small"
                variant="outlined"
              />
            ) : cptMatch === false ? (
              <Chip
                icon={<WarningIcon />}
                label="Mismatch"
                color="warning"
                size="small"
                variant="outlined"
              />
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                —
              </Typography>
            )}
          </Box>

          {/* AI Recommendations */}
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
            >
              AI{' '}
              {hasRecommendedCodes && recommendedCodes.length > 1
                ? 'Recommendations'
                : 'Recommendation'}
            </Typography>
            {hasRecommendedCodes ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                {recommendedCodes.map((code, index) => (
                  <Typography
                    key={index}
                    variant="h6"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: hasMismatch ? theme.palette.warning.main : theme.palette.text.primary,
                    }}
                  >
                    {code}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: hasMismatch ? theme.palette.warning.main : theme.palette.text.primary,
                }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>

        {/* Mismatch Alert */}
        {hasMismatch && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              onUpdateCPT && hasRecommendedCodes && recommendedCodes.length > 0 ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => onUpdateCPT(recommendedCodes[0])}
                >
                  Update CPT
                </Button>
              ) : undefined
            }
          >
            The AI recommends different CPT code
            {hasRecommendedCodes && recommendedCodes.length > 1 ? 's' : ''}. Review the
            documentation to ensure proper billing.
          </Alert>
        )}

        {/* Confidence */}
        {billingSupport && (
          <>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  AI Confidence
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {confidencePercent}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={confidencePercent}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      confidencePercent >= 80
                        ? theme.palette.success.main
                        : confidencePercent >= 60
                          ? theme.palette.warning.main
                          : theme.palette.error.main,
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Strengths */}
            {billingSupport.strengths && billingSupport.strengths.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <StrengthIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Strengths
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {billingSupport.strengths.map((strength, i) => (
                    <Typography
                      component="li"
                      key={i}
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
                    >
                      {strength}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Concerns */}
            {billingSupport.concerns && billingSupport.concerns.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <ConcernIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Concerns
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {billingSupport.concerns.map((concern, i) => (
                    <Typography
                      component="li"
                      key={i}
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
                    >
                      {concern}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Missing Elements */}
            {billingSupport.missing_elements_for_code &&
              billingSupport.missing_elements_for_code.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Missing Elements for Higher Code
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {billingSupport.missing_elements_for_code.map((element, i) => (
                      <Chip
                        key={i}
                        label={element}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: theme.palette.grey[300] }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default CPTReviewPanel;
